import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { VariantConfigs } from '../../context/ResponseContext';
import './styles.css';
import { IContextValues } from '../../types';
import { baseUrl } from '../../constants/ssoConstants';
import AppConfig from '../../AppConfig';

const InitialPage = () => {
  const navigate = useNavigate();
  const [botName, setBotName] = useState('');
  const [keyValues, setKeyValues] = useState<IContextValues[]>([
    { key: '', value: '' },
  ]);
  const [inputType, setInputType] = useState(''); // Dropdown selection for collectionURL or articleURL
  const [inputValue, setInputValue] = useState(''); // Text input for the selected dropdown value
  const [metadataJSON, setMetadataJSON] = useState('');
  const [isBuildingContext, setIsBuildingContext] = useState(false);

  const handleBotNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setBotName(e.target.value);
  };

  const handleKeyValueChange = (
    index: number,
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setKeyValues((prevKeyValues) => {
      const newKeyValues = [...prevKeyValues];
      newKeyValues[index] = {
        ...newKeyValues[index],
        [e.target.name as keyof IContextValues]: e.target.value,
      };
      return newKeyValues;
    });
  };

  const handleAddKeyValue = () => {
    setKeyValues([...keyValues, { key: '', value: '' }]);
  };

  const handleDropdownChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setInputType(e.target.value);
    setInputValue(''); // Reset input value when dropdown changes
  };

  const handleInputValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handleMetadataJSONChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    setMetadataJSON(e.target.value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Check if metadata is provided and valid (but make it optional)
    let metadata;
    if (metadataJSON.trim()) {
      try {
        metadata = JSON.parse(metadataJSON);

        // Ensure metadata has both contentTag and source if provided
        if (!metadata.contentTag || !metadata.source) {
          alert(
            'Both contentTag and source must be present in the metadataJSON.'
          );
          return; // Prevent form submission if validation fails
        }
      } catch (err) {
        alert('Invalid JSON format in metadata.');
        return;
      }
    }

    const contextValues: Record<string, any> = keyValues.reduce(
      (acc: Record<string, any>, { key, value }) => {
        if (key) {
          acc[key] = value;
        }
        return acc;
      },
      {}
    );

    const config = VariantConfigs.get(botName);
    if (config) {
      const updatedConfig = { ...config, contextValues };
      VariantConfigs.set(botName, updatedConfig);
    } else {
      const formattedBotName = botName
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
      VariantConfigs.set(botName, {
        variant: botName,
        contextValues: contextValues,
        getInitialMessage: () =>
          `Hello I am Plum's ${formattedBotName}, how I can help you.`,
      });
    }

    // Build context only if inputType (articleURL or collectionURL) is provided
    if (inputType === 'articleURL' || inputType === 'collectionURL') {
      try {
        setIsBuildingContext(true);
        await fetch(`${baseUrl}/policygpt/framework/${botName}/buildContext`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-plum-token': AppConfig.SERVICE_TOKEN,
          },
          body: JSON.stringify({
            [inputType]: inputValue,
            metadata, // Send metadata if available
          }),
        });
        setIsBuildingContext(false);
        alert('Build context is successful');
        navigate(`/bot/${botName}`);
      } catch (err) {
        alert(err);
        console.error('Error:', err);
      }
    } else {
      // Allow navigation if no inputType and no metadata are provided
      navigate(`/bot/${botName}`);
    }
  };

  return (
    <div className="container">
      <form className="form" onSubmit={handleSubmit}>
        <h1 className="form-title">Create a Bot</h1>
        <div className="form-group bot-name-group">
          <label className="form-label bot-name-label">Bot Name:</label>
          <input
            type="text"
            className="form-input bot-name-input"
            value={botName}
            onChange={handleBotNameChange}
            required
          />
        </div>
        {keyValues.map((kv, index) => (
          <div className="form-group key-value-group" key={index}>
            <label className="form-label key-label">Key:</label>
            <input
              type="text"
              className="form-input key-input"
              name="key"
              value={kv.key}
              onChange={(e) => handleKeyValueChange(index, e)}
            />
            <label className="form-label value-label">Value:</label>
            <input
              type="text"
              className="form-input value-input"
              name="value"
              value={kv.value}
              onChange={(e) => handleKeyValueChange(index, e)}
            />
          </div>
        ))}
        <button
          type="button"
          className="form-button add-button"
          onClick={handleAddKeyValue}
        >
          Add Key value
        </button>

        {/* Dropdown for choosing input type */}
        <div className="form-group mt-4">
          <label>Select Input Type:</label>
          <select
            className="form-input"
            value={inputType}
            onChange={handleDropdownChange}
          >
            <option value="">Select...</option>
            <option value="articleURL">Article URL</option>
            <option value="collectionURL">Collection URL</option>
          </select>
        </div>

        {/* Display input box based on dropdown selection */}
        {inputType && (
          <div className="form-group input-value-group">
            <label>{inputType}:</label>
            <input
              type="text"
              className="form-input"
              value={inputValue}
              onChange={handleInputValueChange}
              placeholder={`Enter ${inputType}`}
            />
          </div>
        )}

        <div className="form-group metadata-json-group">
          <label className="form-label metadata-json-label">
            Metadata (JSON):
          </label>
          <textarea
            className="form-input metadata-json-textarea"
            value={metadataJSON}
            onChange={handleMetadataJSONChange}
            placeholder='{"contentTag": "value", "source": "value"}'
          />
        </div>

        {isBuildingContext && <div>Building context.....</div>}
        <div className="form-buttons button-group">
          <button type="submit" className="form-button submit-button">
            Submit
          </button>
        </div>
      </form>
    </div>
  );
};

export default InitialPage;
